import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  ActionChip,
  Alert,
  AppError,
  Button,
  Card,
  categoricalColorVars,
  categoricalSlotByIndex,
  CountBadge,
  DismissibleChip,
  EmptyState,
  Loader,
  Modal,
  Tag,
  TextInput,
  toast,
  ToggleChip,
} from "@bfc-software/pallet";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import { TFunction } from "i18next";
import { logger } from "@bfc-software/app-toolbox";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { useIssApi } from "@bfc-software/bfc-web-ui/lib/iss-api-client";

export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/outbound/whs/$location/_layout/zones",
)({
  component: RouteComponent,
});

type Zone = {
  id: number;
  name: string;
  abbreviation?: string | null;
  description?: string | null;
  product_type_ids: number[];
};

type ProductType = {
  id: number;
  name: string;
};

const VOWELS = new Set(["A", "E", "I", "O", "U"]);

/**
 * The abbreviation field's default -- the zone's first 3 consonants, e.g.
 * "Produce" -> "PRD". Recomputed as the name changes until the user edits
 * the field directly, at which point their value takes over.
 */
function firstConsonants(name: string, count: number): string {
  const consonants: string[] = [];
  for (const char of name.toUpperCase()) {
    if (consonants.length >= count) break;
    if (char >= "A" && char <= "Z" && !VOWELS.has(char)) consonants.push(char);
  }
  return consonants.join("");
}

/**
 * openapi-react-query throws the parsed ErrorResponse body itself
 * (`{ error, message }`) rather than an `Error`, so `instanceof Error` alone
 * would miss it -- see the analogous helper in items.tsx.
 */
function errorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === "object") {
    const body = error as { message?: unknown; error?: unknown };
    if (typeof body.message === "string" && body.message) return body.message;
    if (typeof body.error === "string" && body.error) return body.error;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/**
 * The zone creation form -- an inline panel injected under the "Create zone"
 * button rather than a modal, so picking product types stays in the same view
 * as the rest of the zone list.
 */
function CreateZonePanel({
  productTypes,
  isAssigned,
  onSubmit,
  onClose,
  t,
}: {
  productTypes: ProductType[];
  isAssigned: (productTypeId: number) => boolean;
  onSubmit: (values: {
    name: string;
    abbreviation: string;
    description: string;
    productTypeIds: number[];
  }) => Promise<void>;
  onClose: () => void;
  t: TFunction;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [abbreviation, setAbbreviation] = useState("");
  const [abbreviationEdited, setAbbreviationEdited] = useState(false);
  const [productTypeIds, setProductTypeIds] = useState<number[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const availableTypes = productTypes.filter((pt) => !isAssigned(pt.id));

  const setTypeSelected = (id: number, isSelected: boolean) => {
    setProductTypeIds((prev) =>
      isSelected
        ? prev.includes(id)
          ? prev
          : [...prev, id]
        : prev.filter((x) => x !== id),
    );
  };

  const handleNameChange = (value: string) => {
    setName(value);
    if (!abbreviationEdited) setAbbreviation(firstConsonants(value, 3));
  };

  const handleAbbreviationChange = (value: string) => {
    const next = value.toUpperCase().slice(0, 3);
    setAbbreviation(next);
    // An emptied field resumes auto-fill from the name rather than staying
    // stuck on a blank value the user backed out of.
    setAbbreviationEdited(next.length > 0);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting || name.trim().length === 0) return;

    setIsSubmitting(true);
    try {
      await onSubmit({ name, abbreviation, description, productTypeIds });
      onClose();
    } catch {
      // onSubmit already reported the failure via toast; keep the panel open
      // so the user can retry without re-entering values.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} data-test-role="create-zone-panel">
      <Card
        variant="panel"
        layout="body-only"
        body={
          <>
            <div className="panel-title">{t("zones.form.createTitle")}</div>

            {/* An arbitrary track list (two 2fr tracks either side of a
                fixed 100px one) has no grid-template-columns utility, so the
                three fields sit in a wrapping flex row instead; the
                abbreviation field takes its width from its content. */}
            <div className="flex flex-wrap gap-16">
              <TextInput
                className="flex-1 min-w-0"
                name="name"
                label={t("zones.form.nameLabel")}
                value={name}
                onChange={(ev) => handleNameChange(ev.target.value)}
                placeholder={t("zones.form.namePlaceholder")}
                autofocus={true}
                isRequired={true}
              />
              {/* `TextInput` has no vocabulary for per-field input styling
                  (mono font, uppercase, centered text) -- its `className`
                  lands on the `TextField` wrapper, not the inner `<input>`,
                  and it exposes no slot to reach the input itself. The
                  abbreviation field's previous mono/uppercase/centered
                  presentation is dropped rather than reached for; the value
                  is still forced to uppercase in `handleAbbreviationChange`. */}
              <TextInput
                className="flex-1 min-w-0"
                name="abbreviation"
                label={t("zones.form.abbreviationLabel")}
                value={abbreviation}
                onChange={(ev) => handleAbbreviationChange(ev.target.value)}
                placeholder={t("zones.form.abbreviationPlaceholder")}
                maxLength={3}
              />
              <TextInput
                className="flex-1 min-w-0"
                name="description"
                label={t("zones.form.descriptionLabel")}
                value={description}
                onChange={(ev) => setDescription(ev.target.value)}
                placeholder={t("zones.form.descriptionPlaceholder")}
              />
            </div>

            <div className="text-size-13 font-strong text-on-surface-variant">
              {t("zones.form.productTypesLabel")}
            </div>
            <div className="flex flex-wrap items-center gap-8">
              {availableTypes.map((pt) => {
                const selected = productTypeIds.includes(pt.id);
                return (
                  <ToggleChip
                    key={pt.id}
                    label={pt.name}
                    isSelected={selected}
                    onChange={(isSelected) =>
                      setTypeSelected(pt.id, isSelected)
                    }
                  />
                );
              })}
              {availableTypes.length === 0 ? (
                <p className="text-size-13 text-on-surface-variant">
                  {t("zones.form.noProductTypes")}
                </p>
              ) : null}
            </div>

            <div className="flex justify-end gap-8">
              <Button
                kind="text-only"
                variant="primary"
                type="submit"
                text={
                  isSubmitting ? t("zones.form.saving") : t("zones.form.submit")
                }
                isDisabled={isSubmitting || name.trim().length === 0}
              />
              <Button
                kind="text-only"
                type="button"
                variant="tertiary"
                text={t("zones.form.cancel")}
                onPress={onClose}
                isDisabled={isSubmitting}
              />
            </div>
          </>
        }
      />
    </form>
  );
}

/**
 * The zone's location count, via ISS's `/slots/list?zoneName=` filter --
 * `limit: 1` so only `pagination.total_count` is paid for, not the rows
 * themselves (the same low-cost-count pattern items.tsx uses for its
 * attribute filter counts).
 *
 * Its own component rather than a query mapped over the zone list: the
 * number of zones changes between renders, and calling a hook once per zone
 * inside `.map()` in the parent would violate the rules of hooks.
 */
function ZoneLocationsCount({
  zoneName,
  client,
}: {
  zoneName: string;
  client: ReturnType<typeof useIssApi>;
}) {
  const query = client.useQuery("get", "/slots/list", {
    params: { query: { zoneName, limit: 1 } },
  });

  if (query.isLoading) return <Loader type="inline" />;
  if (query.isError)
    return (
      <span className="text-size-14 font-heading text-on-surface-variant">
        —
      </span>
    );
  return (
    <span className="text-size-14 font-heading text-on-surface-variant">
      {query.data?.pagination.total_count ?? 0}
    </span>
  );
}

function ZoneCard({
  zone,
  client,
  productTypeName,
  availableToAdd,
  addOpen,
  onToggleAdd,
  onAddType,
  onRemoveType,
  onDelete,
  t,
}: {
  zone: Zone;
  client: ReturnType<typeof useIssApi>;
  productTypeName: Map<number, string>;
  availableToAdd: ProductType[];
  addOpen: boolean;
  onToggleAdd: () => void;
  onAddType: (productTypeId: number) => void;
  onRemoveType: (productTypeId: number) => void;
  onDelete: () => void;
  t: TFunction;
}) {
  // Cycled by id rather than name so a renamed zone keeps its color. Shared
  // by the card's left border and its product type chips, so a zone stays
  // visually recognizable as its chips reappear on other cards while being
  // edited.
  const slot = categoricalSlotByIndex(zone.id);
  // `Card` takes `style` only -- no `className` (SR-301 phase 8 B7/B8) and no
  // categorical-color prop -- so this accent has no class it can be spelled
  // with, and the role resolves to a `var()` string in TS instead. The chips
  // on this same card go through `badgeColorClass`, which is the class-based
  // contract this border would use if `Card` grew the prop.
  const { color } = categoricalColorVars(slot);

  return (
    <Card
      layout="body-only"
      // eslint-disable-next-line react/forbid-component-props -- runtime-computed value
      style={{ borderLeftColor: color, borderLeftWidth: 4 }}
      data-test-role="zone-row"
      body={
        <div className="flex flex-wrap items-start gap-20 py-16 px-20">
          {/* eslint-disable-next-line react/forbid-dom-props -- follow-up: replace hardcoded inline style with flexbox/pallet utilities */}
          <div className="flex flex-col gap-4" style={{ minWidth: 190 }}>
            <div className="flex items-center gap-8">
              <span
                className="text-on-surface text-size-16"
                data-test-role="zone-name"
              >
                {zone.name}
              </span>
              <span
                className="rounded-6 bg-surface-container text-on-surface-variant font-mono text-size-12 font-heading py-4 px-8"
                data-test-role="zone-abbreviation"
              >
                {zone.abbreviation || "—"}
              </span>
            </div>
            {zone.description ? (
              <p className="text-size-13 text-on-surface-variant">
                {zone.description}
              </p>
            ) : null}
          </div>

          {/* eslint-disable-next-line react/forbid-dom-props -- follow-up: replace hardcoded inline style with flexbox/pallet utilities */}
          <div className="flex flex-col gap-4" style={{ minWidth: 110 }}>
            <div className="text-size-13 font-strong text-on-surface-variant">
              {t("zones.locationsLabel")}
            </div>
            <ZoneLocationsCount zoneName={zone.name} client={client} />
          </div>

          {/* eslint-disable-next-line react/forbid-dom-props -- follow-up: replace hardcoded inline style with flexbox/pallet utilities */}
          <div className="flex flex-1 flex-col gap-8" style={{ minWidth: 250 }}>
            <div className="text-size-13 font-strong text-on-surface-variant">
              {t("zones.productTypesLabel")}
            </div>
            <div className="flex flex-wrap items-center gap-8">
              {zone.product_type_ids.map((id) => (
                <DismissibleChip
                  key={id}
                  label={String(productTypeName.get(id) ?? id)}
                  color={{ mode: "categorical", slot }}
                  onDismiss={addOpen ? () => onRemoveType(id) : undefined}
                  dismissLabel={t("zones.rowActions.removeType")}
                />
              ))}
              {zone.product_type_ids.length === 0 ? (
                <span className="text-size-13 text-on-surface-variant">
                  {t("zones.noProductTypesAssigned")}
                </span>
              ) : null}
            </div>

            {addOpen ? (
              <div className="flex flex-wrap items-center gap-8 pt-8 border-t border-dashed border-outline">
                {availableToAdd.map((pt) => (
                  <ActionChip
                    key={pt.id}
                    label={pt.name}
                    onPress={() => onAddType(pt.id)}
                  />
                ))}
                {availableToAdd.length === 0 ? (
                  <span className="text-size-13 text-on-surface-variant">
                    {t("zones.form.noProductTypes")}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="flex items-center gap-4 flex-none ml-auto">
            <Button
              kind="icon-only"
              variant={addOpen ? "filled" : "tertiary"}
              icon={addOpen ? "Check" : "Edit2"}
              label={
                addOpen
                  ? t("zones.rowActions.doneEditing")
                  : t("zones.rowActions.edit")
              }
              onPress={onToggleAdd}
            />
            <Button
              kind="icon-only"
              label={t("zones.rowActions.delete")}
              variant="tertiary"
              icon="Trash"
              onPress={onDelete}
            />
          </div>
        </div>
      }
    />
  );
}

function DeleteZoneConfirm({
  zone,
  isPending,
  onCancel,
  onConfirm,
  t,
}: {
  zone: Zone | undefined;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  t: TFunction;
}) {
  return (
    <Modal
      control="external"
      data-test-role="delete-zone-confirm"
      isDismissable={!isPending}
      isOpen={zone !== undefined}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
      header={<h2>{t("zones.deleteConfirm.title")}</h2>}
      footer={
        <>
          <Button
            kind="text-only"
            text={t("zones.deleteConfirm.cancel")}
            variant="tertiary"
            onPress={onCancel}
            isDisabled={isPending}
          />
          <Button
            kind="text-only"
            text={t("zones.deleteConfirm.confirm")}
            variant="danger"
            onPress={onConfirm}
            isDisabled={isPending}
          />
        </>
      }
    >
      <div className="delete-confirm">
        <p className="delete-confirm-body">{t("zones.deleteConfirm.body")}</p>
        {zone ? <p className="delete-confirm-target">{zone.name}</p> : null}
      </div>
    </Modal>
  );
}

export function RouteComponent() {
  const { lang } = Route.useParams();
  const { t } = useTranslation({
    lang,
    namespace: TRANSLATION_NAMESPACE.outbound,
  });

  const client = useIssApi();

  const zonesQuery = client.useQuery("get", "/zones/all", {});
  const productTypesQuery = client.useQuery("get", "/product-types/all", {});

  const createMutation = client.useMutation("post", "/zones/create");
  const updateMutation = client.useMutation("post", "/zones/update");
  const deleteMutation = client.useMutation("post", "/zones/delete");

  const [createOpen, setCreateOpen] = useState(false);
  const [addOpenFor, setAddOpenFor] = useState<number | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<Zone | undefined>();

  const zones = (zonesQuery.data?.data ?? []) as Zone[];
  const productTypes = (productTypesQuery.data?.data ?? []) as ProductType[];

  const zoneOf = new Map<number, Zone>();
  zones.forEach((zone) =>
    zone.product_type_ids.forEach((id) => zoneOf.set(id, zone)),
  );
  const productTypeName = new Map(productTypes.map((pt) => [pt.id, pt.name]));
  const unassignedTypes = productTypes.filter((pt) => !zoneOf.has(pt.id));

  const isLoading = zonesQuery.isLoading || productTypesQuery.isLoading;
  const isError = zonesQuery.isError || productTypesQuery.isError;

  const refetchAll = () => {
    zonesQuery.refetch();
    productTypesQuery.refetch();
  };

  const handleCreateSubmit = async (values: {
    name: string;
    abbreviation: string;
    description: string;
    productTypeIds: number[];
  }) => {
    try {
      const created = await createMutation.mutateAsync({
        body: {
          name: values.name.trim(),
          abbreviation: values.abbreviation.trim() || null,
          description: values.description.trim() || null,
          product_type_ids: values.productTypeIds,
        },
      });
      logger.info("Created zone", { id: created.data.id });
      toast.success(t("zones.form.createSuccessToast"));
      refetchAll();
    } catch (error) {
      logger.warn("Failed to create zone", { error });
      toast.error(errorMessage(error, t("zones.form.saveFailedFallback")));
      throw error;
    }
  };

  const updateZoneTypes = (zone: Zone, nextIds: number[]) => {
    updateMutation
      .mutateAsync({
        params: { query: { zoneId: zone.id } },
        body: {
          name: zone.name,
          abbreviation: zone.abbreviation ?? null,
          description: zone.description ?? null,
          product_type_ids: nextIds,
        },
      })
      .then(() => {
        logger.info("Updated zone product types", { id: zone.id });
        refetchAll();
      })
      .catch((error: unknown) => {
        logger.warn("Failed to update zone product types", {
          error,
          id: zone.id,
        });
        toast.error(errorMessage(error, t("zones.form.saveFailedFallback")));
      });
  };

  const closeDeleteConfirm = () => {
    if (deleteMutation.isPending) return;
    setDeleteTarget(undefined);
  };

  const confirmDelete = () => {
    if (!deleteTarget || deleteMutation.isPending) return;

    const target = deleteTarget;
    deleteMutation
      .mutateAsync({ params: { query: { zoneId: target.id } } })
      .then(() => {
        logger.info("Deleted zone", { id: target.id });
        toast.success(t("zones.deleteConfirm.successToast"));
        setDeleteTarget(undefined);
        refetchAll();
      })
      .catch((error: unknown) => {
        logger.warn("Failed to delete zone", { error, id: target.id });
        toast.error(errorMessage(error, t("zones.deleteConfirm.errorToast")));
      });
  };

  return (
    <div className="bg-surface h-full w-full">
      <div className="page-shell">
        <header className="page-header">
          <div className="page-header-left">
            <h1 className="page-title">{t("zones.title")}</h1>
            <p className="page-subtitle">{t("zones.subtitle")}</p>
          </div>
        </header>

        {isLoading ? (
          <Loader type="page" />
        ) : isError ? (
          <AppError
            description={t("zones.failed")}
            resolution={t("zones.failureSupport")}
          />
        ) : (
          <>
            <Alert
              severity="info"
              title={t("zones.setupNote", { count: zones.length })}
              reference={
                unassignedTypes.length > 0 ? (
                  <Tag
                    label={t("zones.unassignedBadge", {
                      count: unassignedTypes.length,
                    })}
                    display={{ tone: "warning" }}
                  />
                ) : undefined
              }
            />

            <div className="flex items-center justify-between gap-16 flex-wrap">
              <div className="flex items-center gap-8">
                <h2 className="text-size-18 font-heading text-on-surface">
                  {t("zones.listHeading")}
                </h2>
                <CountBadge count={zones.length} />
              </div>
              <Button
                kind="text-icon-left"
                variant="avatar-surface"
                icon="Plus"
                text={t("zones.newButton")}
                onPress={() => setCreateOpen(true)}
              />
            </div>

            {createOpen ? (
              <CreateZonePanel
                productTypes={productTypes}
                isAssigned={(id) => zoneOf.has(id)}
                onSubmit={handleCreateSubmit}
                onClose={() => setCreateOpen(false)}
                t={t}
              />
            ) : null}

            {zones.length === 0 ? (
              <EmptyState heading={t("zones.empty")} />
            ) : (
              <div className="flex flex-col gap-12">
                {zones.map((zone) => (
                  <ZoneCard
                    key={zone.id}
                    zone={zone}
                    client={client}
                    productTypeName={productTypeName}
                    availableToAdd={unassignedTypes}
                    addOpen={addOpenFor === zone.id}
                    onToggleAdd={() =>
                      setAddOpenFor((prev) =>
                        prev === zone.id ? undefined : zone.id,
                      )
                    }
                    onAddType={(typeId) =>
                      updateZoneTypes(zone, [...zone.product_type_ids, typeId])
                    }
                    onRemoveType={(typeId) =>
                      updateZoneTypes(
                        zone,
                        zone.product_type_ids.filter((id) => id !== typeId),
                      )
                    }
                    onDelete={() => setDeleteTarget(zone)}
                    t={t}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <DeleteZoneConfirm
        zone={deleteTarget}
        isPending={deleteMutation.isPending}
        onCancel={closeDeleteConfirm}
        onConfirm={confirmDelete}
        t={t}
      />
    </div>
  );
}
